import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

const api = new API();


/*
|--------------------------------------------------------------------------
| DEFAULTS
|--------------------------------------------------------------------------
*/

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
};

const DEFAULT_SUMMARY = {
    totalPayments: 0,
    totalAmount: 0,
    allocatedAmount: 0,
    unallocatedAmount: 0,
};


/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    if (
        typeof error ===
        "string"
    ) {
        return error;
    }

    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};


/*
|--------------------------------------------------------------------------
| NORMALIZE API RESPONSE
|--------------------------------------------------------------------------
|
| Supports all of these response shapes:
|
| 1.
|
| {
|     data: [...],
|     pagination: {},
|     summary: {}
| }
|
| 2.
|
| {
|     success: true,
|     data: {
|         records: [],
|         pagination: {},
|         summary: {}
|     }
| }
|
| 3. Axios-style:
|
| {
|     status: 200,
|     data: {
|         success: true,
|         data: {
|             records: [],
|             pagination: {},
|             summary: {}
|         }
|     }
| }
|--------------------------------------------------------------------------
*/

const getResponseEnvelope = (
    response
) => {
    if (
        !response
    ) {
        return {};
    }

    /*
    |--------------------------------------------------------------------------
    | Axios-style response
    |--------------------------------------------------------------------------
    */

    if (
        response?.data &&
        typeof response.data ===
            "object" &&
        !Array.isArray(
            response.data
        ) &&
        (
            response?.status !==
                undefined ||
            response?.headers !==
                undefined ||
            response?.config !==
                undefined
        )
    ) {
        return response.data;
    }

    return response;
};


/*
|--------------------------------------------------------------------------
| GET RESPONSE DATA
|--------------------------------------------------------------------------
|
| Used by detail/create/reverse APIs.
|--------------------------------------------------------------------------
*/

const getResponseData = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    return (
        envelope?.data ??
        envelope
    );
};


/*
|--------------------------------------------------------------------------
| GET RECORDS
|--------------------------------------------------------------------------
*/

const getRecords = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    const data =
        envelope?.data ??
        envelope;

    /*
    |--------------------------------------------------------------------------
    | data itself is array
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            data
        )
    ) {
        return data;
    }

    /*
    |--------------------------------------------------------------------------
    | data.records
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            data?.records
        )
    ) {
        return data.records;
    }

    /*
    |--------------------------------------------------------------------------
    | envelope.records
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            envelope?.records
        )
    ) {
        return envelope.records;
    }

    return [];
};


/*
|--------------------------------------------------------------------------
| GET PAGINATION
|--------------------------------------------------------------------------
*/

const getPagination = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    const data =
        envelope?.data ??
        {};

    const pagination =
        envelope?.pagination ||
        data?.pagination ||
        {};

    return {
        page:
            Number(
                pagination?.page
            ) || 1,

        limit:
            Number(
                pagination?.limit
            ) || 20,

        total:
            Number(
                pagination?.total
            ) || 0,

        totalPages:
            Math.max(
                Number(
                    pagination?.totalPages
                ) || 1,
                1
            ),

        hasNextPage:
            Boolean(
                pagination
                    ?.hasNextPage
            ),

        hasPreviousPage:
            Boolean(
                pagination
                    ?.hasPreviousPage
            ),
    };
};


/*
|--------------------------------------------------------------------------
| GET SUMMARY
|--------------------------------------------------------------------------
*/

const getSummary = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    const data =
        envelope?.data ??
        {};

    return (
        envelope?.summary ||
        data?.summary ||
        {}
    );
};


/*
|--------------------------------------------------------------------------
| BUILD QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value ===
                    undefined ||
                value ===
                    null ||
                value ===
                    ""
            ) {
                return;
            }

            query.append(
                key,
                value
            );
        }
    );

    const queryString =
        query.toString();

    return queryString
        ? `?${queryString}`
        : "";
};


/*
|--------------------------------------------------------------------------
| REVERSE PAYMENT OUT
|--------------------------------------------------------------------------
*/

export const reversePaymentOut =
    createAsyncThunk(
        "paymentOut/reversePaymentOut",

        async (
            {
                paymentOutId,
                reversalReason,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !paymentOutId
                ) {
                    return rejectWithValue(
                        "Payment Out ID is required."
                    );
                }

                const normalizedReason =
                    String(
                        reversalReason ||
                        ""
                    ).trim();

                if (
                    !normalizedReason
                ) {
                    return rejectWithValue(
                        "Reversal reason is required."
                    );
                }

                const response =
                    await api.patch(
                        config.reversePaymentOut(
                            paymentOutId
                        ),
                        {
                            reversalReason:
                                normalizedReason,
                        }
                    );

                const data =
                    getResponseData(
                        response
                    );

                return (
                    data ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to reverse Payment Out."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH PAYMENT OUT LIST
|--------------------------------------------------------------------------
*/

export const fetchPaymentOutList =
    createAsyncThunk(
        "paymentOut/fetchList",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        search:
                            params.search,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        paymentMethod:
                            params.paymentMethod,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,
                    });

                const response =
                    await api.get(
                        `${config.getPaymentOutList()}${queryString}`
                    );

                return {
                    records:
                        getRecords(
                            response
                        ),

                    pagination:
                        getPagination(
                            response
                        ),

                    summary:
                        getSummary(
                            response
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier payments."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH PAYMENT OUT BY ID
|--------------------------------------------------------------------------
*/

export const fetchPaymentOutById =
    createAsyncThunk(
        "paymentOut/fetchById",

        async (
            paymentOutId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !paymentOutId
                ) {
                    return rejectWithValue(
                        "Payment ID is required."
                    );
                }

                const response =
                    await api.get(
                        config.getPaymentOutById(
                            paymentOutId
                        )
                    );

                const data =
                    getResponseData(
                        response
                    );

                /*
                |--------------------------------------------------------------------------
                | Some APIs wrap detail under paymentOut
                |--------------------------------------------------------------------------
                */

                return (
                    data?.paymentOut ||
                    data?.payment ||
                    data ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier payment."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH SUPPLIER OUTSTANDING PURCHASE BILLS
|--------------------------------------------------------------------------
*/

export const fetchSupplierOutstandingPurchaseBills =
    createAsyncThunk(
        "paymentOut/fetchOutstandingBills",

        async (
            {
                supplierId,
                search = "",
                limit = 100,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                const queryString =
                    buildQueryString({
                        search,
                        limit,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierOutstandingPurchaseBills(
                            supplierId
                        )}${queryString}`
                    );

                return {
                    records:
                        getRecords(
                            response
                        ),

                    summary:
                        getSummary(
                            response
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load outstanding purchase bills."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| CREATE PAYMENT OUT
|--------------------------------------------------------------------------
*/

export const createPaymentOut =
    createAsyncThunk(
        "paymentOut/create",

        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !payload
                        ?.supplierId
                ) {
                    return rejectWithValue(
                        "Supplier is required."
                    );
                }

                if (
                    Number(
                        payload?.amount ||
                        0
                    ) <= 0
                ) {
                    return rejectWithValue(
                        "Payment amount must be greater than zero."
                    );
                }

                const response =
                    await api.post(
                        config.createPaymentOut(),
                        payload
                    );

                const data =
                    getResponseData(
                        response
                    );

                return (
                    data?.paymentOut ||
                    data?.payment ||
                    data ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to record supplier payment."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    /*
    |--------------------------------------------------------------------------
    | PAYMENT LIST
    |--------------------------------------------------------------------------
    */

    records: [],

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    pagination: {
        ...DEFAULT_PAGINATION,
    },

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    summary: {
        ...DEFAULT_SUMMARY,
    },

    /*
    |--------------------------------------------------------------------------
    | PAYMENT DETAILS
    |--------------------------------------------------------------------------
    */

    selectedPayment:
        null,

    /*
    |--------------------------------------------------------------------------
    | OUTSTANDING PURCHASE BILLS
    |--------------------------------------------------------------------------
    */

    outstandingBills:
        [],

    outstandingSummary: {
        totalBills: 0,
        totalOutstanding: 0,
    },

    /*
    |--------------------------------------------------------------------------
    | CREATE RESULT
    |--------------------------------------------------------------------------
    */

    createdPayment:
        null,

    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    loading: {
        list: false,
        details: false,
        outstandingBills: false,
        create: false,
        reverse: false,
    },

    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    error: {
        list: null,
        details: null,
        outstandingBills: null,
        create: null,
        reverse: null,
    },
};


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const paymentOutSlice =
    createSlice({
        name:
            "paymentOut",

        initialState,

        reducers: {
            /*
            |--------------------------------------------------------------------------
            | CLEAR SELECTED PAYMENT
            |--------------------------------------------------------------------------
            */

            clearSelectedPaymentOut: (
                state
            ) => {
                state.selectedPayment =
                    null;

                state.error.details =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR OUTSTANDING
            |--------------------------------------------------------------------------
            */

            clearPaymentOutOutstandingBills: (
                state
            ) => {
                state.outstandingBills =
                    [];

                state.outstandingSummary =
                {
                    totalBills:
                        0,

                    totalOutstanding:
                        0,
                };

                state.error.outstandingBills =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR CREATED
            |--------------------------------------------------------------------------
            */

            clearCreatedPaymentOut: (
                state
            ) => {
                state.createdPayment =
                    null;

                state.error.create =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ERRORS
            |--------------------------------------------------------------------------
            */

            clearPaymentOutErrors: (
                state
            ) => {
                state.error = {
                    list:
                        null,

                    details:
                        null,

                    outstandingBills:
                        null,

                    create:
                        null,

                    reverse:
                        null,
                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET
            |--------------------------------------------------------------------------
            */

            resetPaymentOutState:
                () => ({
                    ...initialState,

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },

                    summary: {
                        ...DEFAULT_SUMMARY,
                    },

                    outstandingSummary: {
                        totalBills:
                            0,

                        totalOutstanding:
                            0,
                    },

                    loading: {
                        ...initialState
                            .loading,
                    },

                    error: {
                        ...initialState
                            .error,
                    },
                }),
        },


        /*
        |--------------------------------------------------------------------------
        | EXTRA REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | FETCH LIST
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPaymentOutList
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.list =
                            true;

                        state.error.list =
                            null;
                    }
                )

                .addCase(
                    fetchPaymentOutList
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.records =
                            Array.isArray(
                                action
                                    .payload
                                    ?.records
                            )
                                ? action
                                    .payload
                                    .records
                                : [];

                        state.pagination = {
                            ...DEFAULT_PAGINATION,

                            ...(action
                                .payload
                                ?.pagination ||
                                {}),
                        };

                        state.summary = {
                            ...DEFAULT_SUMMARY,

                            ...(action
                                .payload
                                ?.summary ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchPaymentOutList
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.error.list =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier payments.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | FETCH DETAILS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPaymentOutById
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.details =
                            true;

                        state.error.details =
                            null;

                        state.selectedPayment =
                            null;
                    }
                )

                .addCase(
                    fetchPaymentOutById
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.selectedPayment =
                            action.payload ||
                            null;
                    }
                )

                .addCase(
                    fetchPaymentOutById
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.error.details =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier payment.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | OUTSTANDING BILLS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierOutstandingPurchaseBills
                        .pending,
                    (
                        state
                    ) => {
                        state.loading
                            .outstandingBills =
                            true;

                        state.error
                            .outstandingBills =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierOutstandingPurchaseBills
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .outstandingBills =
                            false;

                        state.outstandingBills =
                            Array.isArray(
                                action
                                    .payload
                                    ?.records
                            )
                                ? action
                                    .payload
                                    .records
                                : [];

                        state.outstandingSummary =
                        {
                            totalBills:
                                0,

                            totalOutstanding:
                                0,

                            ...(action
                                .payload
                                ?.summary ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchSupplierOutstandingPurchaseBills
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .outstandingBills =
                            false;

                        state.outstandingBills =
                            [];

                        state.outstandingSummary =
                        {
                            totalBills:
                                0,

                            totalOutstanding:
                                0,
                        };

                        state.error
                            .outstandingBills =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load outstanding purchase bills.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | CREATE PAYMENT OUT
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    createPaymentOut
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.create =
                            true;

                        state.error.create =
                            null;

                        state.createdPayment =
                            null;
                    }
                )

                .addCase(
                    createPaymentOut
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        state.createdPayment =
                            action.payload ||
                            null;

                        /*
                        |--------------------------------------------------------------------------
                        | OPTIMISTIC LIST UPDATE
                        |--------------------------------------------------------------------------
                        */

                        if (
                            action.payload
                                ?._id
                        ) {
                            state.records =
                            [
                                action.payload,

                                ...state.records.filter(
                                    (
                                        item
                                    ) =>
                                        String(
                                            item
                                                ?._id ||
                                            ""
                                        ) !==
                                        String(
                                            action
                                                .payload
                                                ._id
                                        )
                                ),
                            ];

                            state.pagination.total =
                                Number(
                                    state
                                        .pagination
                                        .total ||
                                    0
                                ) + 1;
                        }
                    }
                )

                .addCase(
                    createPaymentOut
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        state.error.create =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to record supplier payment.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | REVERSE PAYMENT OUT
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    reversePaymentOut
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.reverse =
                            true;

                        state.error.reverse =
                            null;
                    }
                )

                .addCase(
                    reversePaymentOut
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.reverse =
                            false;

                        /*
                        |--------------------------------------------------------------------------
                        | Backend may return:
                        |
                        | {
                        |     paymentOut: {},
                        |     reversalLedgerEntry: {}
                        | }
                        |
                        | or just payment
                        |--------------------------------------------------------------------------
                        */

                        const updatedPayment =
                            action.payload
                                ?.paymentOut ||
                            action.payload
                                ?.payment ||
                            action.payload;

                        if (
                            !updatedPayment
                                ?._id
                        ) {
                            return;
                        }

                        /*
                        |--------------------------------------------------------------------------
                        | FIX:
                        |
                        | State uses selectedPayment.
                        | Do NOT use selectedPaymentOut.
                        |--------------------------------------------------------------------------
                        */

                        state.selectedPayment =
                            updatedPayment;

                        state.records =
                            state.records.map(
                                (
                                    payment
                                ) =>
                                    String(
                                        payment
                                            ?._id ||
                                        ""
                                    ) ===
                                    String(
                                        updatedPayment
                                            ._id
                                    )
                                        ? updatedPayment
                                        : payment
                            );
                    }
                )

                .addCase(
                    reversePaymentOut
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.reverse =
                            false;

                        state.error.reverse =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to reverse Payment Out.";
                    }
                );
        },
    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearSelectedPaymentOut,

    clearPaymentOutOutstandingBills,

    clearCreatedPaymentOut,

    clearPaymentOutErrors,

    resetPaymentOutState,
} =
    paymentOutSlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
|
| Root reducer:
|
| paymentOut: paymentOutReducer
|--------------------------------------------------------------------------
*/

export const paymentOutStateSelector = (
    state
) =>
    state?.paymentOut ||
    initialState;


export const paymentOutListSelector = (
    state
) =>
    state?.paymentOut
        ?.records ||
    [];


export const paymentOutPaginationSelector = (
    state
) =>
    state?.paymentOut
        ?.pagination ||
    initialState.pagination;


export const paymentOutSummarySelector = (
    state
) =>
    state?.paymentOut
        ?.summary ||
    initialState.summary;


export const selectedPaymentOutSelector = (
    state
) =>
    state?.paymentOut
        ?.selectedPayment ||
    null;


export const paymentOutOutstandingBillsSelector = (
    state
) =>
    state?.paymentOut
        ?.outstandingBills ||
    [];


export const paymentOutOutstandingSummarySelector = (
    state
) =>
    state?.paymentOut
        ?.outstandingSummary ||
    initialState
        .outstandingSummary;


export const createdPaymentOutSelector = (
    state
) =>
    state?.paymentOut
        ?.createdPayment ||
    null;


export const paymentOutLoadingSelector = (
    state
) =>
    state?.paymentOut
        ?.loading ||
    initialState.loading;


export const paymentOutErrorSelector = (
    state
) =>
    state?.paymentOut
        ?.error ||
    initialState.error;


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default paymentOutSlice.reducer;